import { useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { ArrowLeft, Users, CreditCard, TrendingUp, Shield, AlertTriangle, Tag, FileText, Download, Globe, BarChart3, Activity, FileDown, Eye, Send, Mail, Settings, MapPin, PackageOpen, Monitor, Smartphone, UserX, Trash2, RotateCcw, Calendar, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { FlagIcon, countryNames } from '../components/FlagIcon';
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { AdminBlogManager } from '../components/AdminBlogManager';
import { AdminLicenses } from '../components/admin/AdminLicenses';
import { AdminFraudAlerts } from '../components/admin/AdminFraudAlerts';
import { AdminCampaigns } from '../components/admin/AdminCampaigns';
import { API } from '../config';
import { exportCSV, exportPDF, exportChartPDF } from '../utils/exportUtils';

const TABS = [
  { id: 'overview', label: "Vue d'ensemble", icon: TrendingUp },
  { id: 'analytics', label: 'Visiteurs', icon: Globe },
  { id: 'accounts', label: 'Comptes', icon: Users },
  { id: 'fraud', label: 'Fraude', icon: AlertTriangle },
  { id: 'campaigns', label: 'Campagnes', icon: Tag },
  { id: 'transactions', label: 'Transactions', icon: CreditCard },
  { id: 'referrals', label: 'Parrainages', icon: Users },
  { id: 'updates', label: 'Mises à jour', icon: PackageOpen },
  { id: 'reports', label: 'Rapports', icon: BarChart3 },
  { id: 'roadmap', label: 'Roadmap', icon: Activity },
  { id: 'resources', label: 'Ressources', icon: FileText },
  { id: 'blog', label: 'Blog', icon: FileText },
];

const CHART_CONFIGS = [
  { key: 'users', label: 'Utilisateurs', color: '#10b981', gradient: 'emerald' },
  { key: 'entitlements', label: 'Licences', color: '#3b82f6', gradient: 'blue' },
  { key: 'activations', label: 'Appareils', color: '#06b6d4', gradient: 'cyan' },
  { key: 'transactions', label: 'Transactions', color: '#f59e0b', gradient: 'amber' },
];

export default function AdminPage() {
  const { user, loading, getToken } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [activeChart, setActiveChart] = useState(null);
  const [chartType, setChartType] = useState('area');
  const [chartPeriod, setChartPeriod] = useState(30);
  const [txData, setTxData] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsPeriod, setAnalyticsPeriod] = useState(30);
  const [countriesData, setCountriesData] = useState([]);
  const [pagesData, setPagesData] = useState([]);
  const [eventsData, setEventsData] = useState([]);
  const overviewChartRef = useRef(null);
  const analyticsChartRef = useRef(null);
  const fileInputRef = useRef(null);
  const [reportPreview, setReportPreview] = useState(null);
  const [reportConfig, setReportConfig] = useState(null);
  const [reportSending, setReportSending] = useState(false);
  const [referralData, setReferralData] = useState(null);
  const [roadmapItems, setRoadmapItems] = useState([]);
  const [roadmapForm, setRoadmapForm] = useState({ title: '', description: '', status: 'planned', category: 'feature' });
  const [editingRoadmap, setEditingRoadmap] = useState(null);
  const [changelogEntries, setChangelogEntries] = useState([]);
  const [changelogForm, setChangelogForm] = useState({ version: '', date: '', tag: '', items: '' });
  const [editingChangelog, setEditingChangelog] = useState(null);
  const [adminResources, setAdminResources] = useState([]);
  const [resourceForm, setResourceForm] = useState({ title: '', description: '', category: 'guide', url: '', content: '', language: 'all' });
  const [editingResource, setEditingResource] = useState(null);
  const [updatesData, setUpdatesData] = useState([]);
  const [updateForm, setUpdateForm] = useState({ platform: '', version: '', release_notes: '', file: null });
  const [uploadingUpdate, setUploadingUpdate] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState('');
  const [editingUpdate, setEditingUpdate] = useState(null);
  const [products, setProducts] = useState([]);
  const [distributions, setDistributions] = useState(null);
  const [purgePreview, setPurgePreview] = useState(null);
  const [purging, setPurging] = useState(false);

  useEffect(() => {
    if (!loading && (!user || !user.roles?.includes('admin'))) navigate('/account');
  }, [user, loading, navigate]);

  const token = getToken();
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!user || !user.roles?.includes('admin')) return;
    axios.get(`${API}/admin/stats`, { headers }).then(r => setStats(r.data)).catch(() => {});
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'transactions' && !txData)
      axios.get(`${API}/admin/transactions`, { headers }).then(r => setTxData(r.data)).catch(() => {});
    if (activeTab === 'referrals' && !referralData)
      axios.get(`${API}/admin/referrals`, { headers }).then(r => setReferralData(r.data)).catch(() => {});
    if (activeTab === 'roadmap') {
      axios.get(`${API}/admin/roadmap`, { headers }).then(r => setRoadmapItems(r.data.items || [])).catch(() => {});
      axios.get(`${API}/admin/changelog`, { headers }).then(r => setChangelogEntries(r.data.entries || [])).catch(() => {});
    }
    if (activeTab === 'resources') {
      axios.get(`${API}/admin/resources`, { headers }).then(r => setAdminResources(r.data.resources || [])).catch(() => {});
    }
    if (activeTab === 'updates') {
      axios.get(`${API}/admin/updates`, { headers }).then(r => setUpdatesData(r.data.updates || [])).catch(() => {});
      axios.get(`${API}/admin/products`, { headers }).then(r => setProducts(r.data.products || [])).catch(() => {});
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadChart = (key) => {
    if (activeChart === key) { setActiveChart(null); return; }
    setActiveChart(key);
    Promise.all([
      axios.get(`${API}/admin/charts?days=${chartPeriod}`, { headers }),
      axios.get(`${API}/admin/analytics/distributions?days=${chartPeriod}`, { headers }),
    ]).then(([c, d]) => { setCharts(c.data.data); setDistributions(d.data); }).catch(() => {});
  };

  useEffect(() => {
    if (activeChart) {
      Promise.all([
        axios.get(`${API}/admin/charts?days=${chartPeriod}`, { headers }),
        axios.get(`${API}/admin/analytics/distributions?days=${chartPeriod}`, { headers }),
      ]).then(([c, d]) => { setCharts(c.data.data); setDistributions(d.data); }).catch(() => {});
    }
  }, [chartPeriod]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAnalytics = () => {
    const d = analyticsPeriod;
    Promise.all([
      axios.get(`${API}/admin/analytics/overview?days=${d}`, { headers }),
      axios.get(`${API}/admin/analytics/countries?days=${d}`, { headers }),
      axios.get(`${API}/admin/analytics/pages?days=${d}`, { headers }),
      axios.get(`${API}/admin/analytics/events?days=${d}`, { headers }),
    ]).then(([ov, co, pg, ev]) => {
      setAnalyticsData(ov.data);
      setCountriesData(co.data.countries || []);
      setPagesData(pg.data.pages || []);
      setEventsData(ev.data.events || []);
    }).catch(() => {});
  };

  useEffect(() => {
    if (activeTab === 'analytics') loadAnalytics();
  }, [activeTab, analyticsPeriod]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const statCards = stats ? [
    { label: 'Utilisateurs', value: stats.users, icon: Users, color: 'text-emerald-400', bg: 'bg-emerald-500/10', chartKey: 'users' },
    { label: 'Licences actives', value: stats.entitlements, icon: Shield, color: 'text-blue-400', bg: 'bg-blue-500/10', chartKey: 'entitlements' },
    { label: 'Appareils actifs', value: stats.activations, icon: TrendingUp, color: 'text-cyan-400', bg: 'bg-cyan-500/10', chartKey: 'activations' },
    { label: 'Paiements', value: stats.paid, icon: CreditCard, color: 'text-amber-400', bg: 'bg-amber-500/10', chartKey: 'transactions' },
    { label: 'Campagnes', value: stats.campaigns, icon: Tag, color: 'text-violet-400', bg: 'bg-violet-500/10' },
    { label: 'Alertes fraude', value: stats.fraud_alerts, icon: AlertTriangle,
      color: stats.fraud_alerts > 0 ? 'text-red-400' : 'text-emerald-400',
      bg: stats.fraud_alerts > 0 ? 'bg-red-500/10' : 'bg-emerald-500/10' },
  ] : [];

  const currentChartConfig = CHART_CONFIGS.find(c => c.key === activeChart);

  return (
    <div className="min-h-screen bg-background" data-testid="admin-page">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <Link to="/account" className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground text-sm mb-6 transition-colors" data-testid="admin-back-link">
          <ArrowLeft className="w-4 h-4" /> Mon compte
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-heading text-3xl font-bold text-foreground mb-6" data-testid="admin-title">Admin Dashboard</h1>

          <div className="flex items-center gap-1 mb-6 overflow-x-auto pb-1 border-b border-border">
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm whitespace-nowrap border-b-2 transition-colors -mb-[1px] ${
                  activeTab === tab.id ? 'border-blue-500 text-blue-400' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`} data-testid={`admin-tab-${tab.id}`}>
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {statCards.map((s, i) => (
                  <div key={i}
                    className={`p-4 rounded-xl ${s.bg} border border-border cursor-pointer transition-all hover:scale-[1.02] ${activeChart === s.chartKey ? 'ring-2 ring-blue-500/50' : ''}`}
                    onClick={() => s.chartKey && loadChart(s.chartKey)}
                    data-testid={`admin-stat-${i}`}>
                    <s.icon className={`w-5 h-5 ${s.color} mb-2`} />
                    <p className="font-heading text-2xl font-bold text-foreground">{s.value ?? '-'}</p>
                    <p className="text-muted-foreground text-xs mt-1">{s.label}</p>
                    {s.chartKey && <p className="text-[10px] text-muted-foreground/50 mt-1">Cliquer pour le graphique</p>}
                  </div>
                ))}
              </div>

              {activeChart && currentChartConfig && charts && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="p-4 rounded-xl bg-card border border-border" data-testid="admin-chart">
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <h3 className="text-foreground text-sm font-medium">
                      {'\u00c9volution \u2014 '}{currentChartConfig.label} ({chartPeriod === 1 ? 'Aujourd\'hui' : `${chartPeriod} jours`})
                    </h3>
                    <div className="flex items-center gap-2">
                      <button onClick={() => exportChartPDF(overviewChartRef.current, `${currentChartConfig.label} - ${chartPeriod} jours`, `graphique_${activeChart}`)}
                        className="flex items-center gap-1 px-2 py-1 rounded-md text-xs text-muted-foreground hover:text-foreground border border-border transition-colors"
                        data-testid="export-overview-chart-pdf">
                        <FileDown className="w-3.5 h-3.5" /> PDF
                      </button>
                      {/* Period selector */}
                      <div className="flex items-center gap-1 bg-secondary rounded-lg p-0.5" data-testid="chart-period-selector">
                        {[{ v: 1, l: '1j' }, { v: 7, l: '7j' }, { v: 30, l: '30j' }, { v: 90, l: '90j' }, { v: 365, l: '1an' }].map(p => (
                          <button key={p.v} onClick={() => setChartPeriod(p.v)}
                            className={`px-2.5 py-1 rounded-md text-xs transition-colors ${chartPeriod === p.v ? 'bg-blue-600 text-white' : 'text-muted-foreground hover:text-foreground'}`}
                            data-testid={`chart-period-${p.v}`}>
                            {p.l}
                          </button>
                        ))}
                      </div>
                      {/* Chart type selector */}
                      <div className="flex items-center gap-1 bg-secondary rounded-lg p-0.5" data-testid="chart-type-selector">
                        {[{ v: 'area', l: 'Ligne' }, { v: 'bar', l: 'Barres' }].map(t => (
                          <button key={t.v} onClick={() => setChartType(t.v)}
                            className={`px-2.5 py-1 rounded-md text-xs transition-colors ${chartType === t.v ? 'bg-blue-600 text-white' : 'text-muted-foreground hover:text-foreground'}`}
                            data-testid={`chart-type-${t.v}`}>
                            {t.l}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div ref={overviewChartRef}>
                  <ResponsiveContainer width="100%" height={250}>
                    {chartType === 'area' ? (
                      <AreaChart data={charts}>
                        <defs>
                          <linearGradient id={`grad-${activeChart}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={currentChartConfig.color} stopOpacity={0.3} />
                            <stop offset="95%" stopColor={currentChartConfig.color} stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                        <XAxis dataKey="date" tick={{ fill: '#71717a', fontSize: 10 }} tickFormatter={v => chartPeriod === 1 ? v.slice(11, 16) : v.slice(5)} />
                        <YAxis tick={{ fill: '#71717a', fontSize: 10 }} />
                        <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #27272a', borderRadius: 8, fontSize: 12 }} labelStyle={{ color: '#a1a1aa' }} />
                        <Area type="monotone" dataKey={activeChart} stroke={currentChartConfig.color} fill={`url(#grad-${activeChart})`} strokeWidth={2} />
                      </AreaChart>
                    ) : (
                      <BarChart data={charts}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                        <XAxis dataKey="date" tick={{ fill: '#71717a', fontSize: 10 }} tickFormatter={v => chartPeriod === 1 ? v.slice(11, 16) : v.slice(5)} />
                        <YAxis tick={{ fill: '#71717a', fontSize: 10 }} />
                        <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #27272a', borderRadius: 8, fontSize: 12 }} labelStyle={{ color: '#a1a1aa' }} />
                        <Bar dataKey={activeChart} fill={currentChartConfig.color} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    )}
                  </ResponsiveContainer>
                  </div>

                  {/* Distribution Pie Charts */}
                  {distributions && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <h4 className="text-foreground text-xs font-medium mb-3">Répartitions</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                        {[
                          { key: 'countries', title: 'Géographie', colors: ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#ec4899','#84cc16','#f97316','#6366f1','#14b8a6','#e11d48','#a855f7','#22d3ee','#facc15'] },
                          { key: 'license_types', title: 'Licences', colors: ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6'] },
                          { key: 'channels', title: 'Canaux', colors: ['#06b6d4','#f59e0b','#10b981','#ef4444','#8b5cf6'] },
                          { key: 'devices', title: 'Appareils', colors: ['#3b82f6','#10b981','#f59e0b','#ef4444'] },
                          { key: 'transactions', title: 'Transactions', colors: ['#10b981','#f59e0b','#ef4444','#6366f1'] },
                        ].map(chart => {
                          const data = distributions[chart.key] || [];
                          if (!data.length) return null;
                          return (
                            <div key={chart.key} className="bg-secondary/30 rounded-lg p-3" data-testid={`overview-pie-${chart.key}`}>
                              <h5 className="text-muted-foreground text-[10px] font-medium mb-2 text-center">{chart.title}</h5>
                              <ResponsiveContainer width="100%" height={120}>
                                <PieChart>
                                  <Pie data={data} cx="50%" cy="50%" innerRadius={28} outerRadius={48} paddingAngle={2} dataKey="value" nameKey="name">
                                    {data.map((_, idx) => <Cell key={idx} fill={chart.colors[idx % chart.colors.length]} />)}
                                  </Pie>
                                  <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #27272a', borderRadius: 8, fontSize: 10 }} formatter={(v, n) => [v, n]} />
                                </PieChart>
                              </ResponsiveContainer>
                              <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-1 justify-center">
                                {data.slice(0, 5).map((d, idx) => (
                                  <div key={idx} className="flex items-center gap-1 text-[9px]">
                                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: chart.colors[idx % chart.colors.length] }} />
                                    <span className="text-muted-foreground truncate max-w-[50px]">{d.name}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </div>
          )}

          {activeTab === 'accounts' && <AdminLicenses token={token} />}
          {activeTab === 'fraud' && <AdminFraudAlerts token={token} />}
          {activeTab === 'campaigns' && <AdminCampaigns token={token} />}

          {activeTab === 'transactions' && (
            <div>
              <div className="flex justify-end gap-2 mb-3">
                <button onClick={() => exportCSV(txData?.transactions?.map(tx => ({
                  Email: tx.user_email, Package: tx.package_id, Produit: tx.product_type || '-',
                  Montant: `${tx.amount} ${tx.currency?.toUpperCase() || ''}`,
                  Promo: tx.promo_code || '', Statut: tx.payment_status,
                  Date: tx.created_at?.slice(0, 10),
                })) || [], 'transactions')}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-border text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="export-transactions-csv-btn">
                  <Download className="w-3.5 h-3.5" /> CSV
                </button>
                <button onClick={() => exportPDF(txData?.transactions?.map(tx => ({
                  Email: tx.user_email, Package: tx.package_id, Produit: tx.product_type || '-',
                  Montant: `${tx.amount} ${tx.currency?.toUpperCase() || ''}`,
                  Promo: tx.promo_code || '', Statut: tx.payment_status,
                  Date: tx.created_at?.slice(0, 10),
                })) || [], 'transactions', 'Transactions')}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-border text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="export-transactions-pdf-btn">
                  <FileDown className="w-3.5 h-3.5" /> PDF
                </button>
              </div>
              <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-transactions-table">
                <thead>
                  <tr className="text-muted-foreground text-xs uppercase tracking-wider border-b border-border">
                    <th className="text-left py-2 pr-4">Email</th>
                    <th className="text-left py-2 pr-4">Package</th>
                    <th className="text-left py-2 pr-4">Produit</th>
                    <th className="text-left py-2 pr-4">Montant</th>
                    <th className="text-left py-2 pr-4">Promo</th>
                    <th className="text-left py-2 pr-4">Statut</th>
                    <th className="text-left py-2">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {txData?.transactions?.map((tx, i) => (
                    <tr key={i} className="border-b border-border/50 last:border-0">
                      <td className="py-2.5 pr-4 text-foreground/80">{tx.user_email}</td>
                      <td className="py-2.5 pr-4 text-muted-foreground">{tx.package_id}</td>
                      <td className="py-2.5 pr-4">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400">{tx.product_type || '-'}</span>
                      </td>
                      <td className="py-2.5 pr-4 text-foreground font-medium">
                        {tx.promo_code && tx.original_amount && (
                          <span className="line-through text-muted-foreground mr-1 text-xs">{tx.original_amount}</span>
                        )}
                        {tx.amount} {tx.currency?.toUpperCase()}
                      </td>
                      <td className="py-2.5 pr-4">
                        {tx.promo_code ? <span className="text-xs px-2 py-0.5 rounded-full bg-violet-500/10 text-violet-400">{tx.promo_code}</span> : '-'}
                      </td>
                      <td className="py-2.5 pr-4">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          tx.payment_status === 'paid' ? 'bg-emerald-500/10 text-emerald-400' :
                          tx.payment_status === 'initiated' ? 'bg-amber-500/10 text-amber-400' :
                          'bg-secondary text-muted-foreground'
                        }`}>{tx.payment_status}</span>
                      </td>
                      <td className="py-2.5 text-muted-foreground">{new Date(tx.created_at).toLocaleDateString('fr-FR')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(!txData?.transactions?.length) && <p className="text-muted-foreground text-sm py-3 text-center">Aucune transaction.</p>}
              </div>
            </div>
          )}

          {activeTab === 'analytics' && (
            <div data-testid="admin-analytics-tab">
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-foreground font-medium text-sm">Tracking visiteurs</h3>
                <div className="flex items-center gap-1 bg-secondary rounded-lg p-0.5 ml-auto" data-testid="analytics-period-selector">
                  {[{ v: 1, l: '1j' }, { v: 7, l: '7j' }, { v: 30, l: '30j' }, { v: 90, l: '90j' }, { v: 365, l: '1an' }].map(p => (
                    <button key={p.v} onClick={() => setAnalyticsPeriod(p.v)}
                      className={`px-2.5 py-1 rounded-md text-xs transition-colors ${analyticsPeriod === p.v ? 'bg-blue-600 text-white' : 'text-muted-foreground hover:text-foreground'}`}>
                      {p.l}
                    </button>
                  ))}
                </div>
              </div>
              {analyticsData && (
                <>
                  <div className="grid grid-cols-3 gap-3 mb-6">
                    {[
                      { label: 'Pages vues', value: analyticsData.totals.views, color: 'text-blue-400' },
                      { label: 'Visiteurs uniques', value: analyticsData.totals.unique_visitors, color: 'text-emerald-400' },
                      { label: 'Requêtes bots', value: analyticsData.totals.bot_views, color: 'text-amber-400' },
                    ].map((s, i) => (
                      <div key={i} className="bg-card border border-border rounded-xl p-4" data-testid={`analytics-stat-${i}`}>
                        <p className="text-muted-foreground text-xs">{s.label}</p>
                        <p className={`font-heading text-2xl font-bold ${s.color}`}>{s.value.toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                  {analyticsData.daily?.length > 0 && (
                    <div className="bg-card border border-border rounded-xl p-4 mb-6" ref={analyticsChartRef}>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-foreground text-xs font-medium">Pages vues ({analyticsPeriod === 1 ? 'Aujourd\'hui' : `${analyticsPeriod}j`})</h4>
                        <button onClick={() => exportChartPDF(analyticsChartRef.current, `Visiteurs - ${analyticsPeriod} jours`, 'graphique_visiteurs')}
                          className="flex items-center gap-1 px-2 py-1 rounded-md text-xs text-muted-foreground hover:text-foreground border border-border transition-colors"
                          data-testid="export-analytics-chart-pdf">
                          <FileDown className="w-3.5 h-3.5" /> PDF
                        </button>
                      </div>
                      <ResponsiveContainer width="100%" height={200}>
                        <AreaChart data={analyticsData.daily}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                          <XAxis dataKey="date" tick={{ fill: '#71717a', fontSize: 10 }} tickFormatter={v => analyticsPeriod === 1 ? v : v.slice(5)} />
                          <YAxis tick={{ fill: '#71717a', fontSize: 10 }} />
                          <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #27272a', borderRadius: 8, fontSize: 12 }} />
                          <Area type="monotone" dataKey="views" stroke="#3b82f6" fill="url(#gradViews)" strokeWidth={2} />
                          <Area type="monotone" dataKey="bot_views" stroke="#f59e0b" fill="url(#gradBots)" strokeWidth={1} opacity={0.5} />
                          <defs>
                            <linearGradient id="gradViews" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/><stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/></linearGradient>
                            <linearGradient id="gradBots" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#f59e0b" stopOpacity={0.2}/><stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/></linearGradient>
                          </defs>
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2 bg-card border border-border rounded-xl p-4" data-testid="analytics-countries">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-foreground text-xs font-medium flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-blue-400" /> Visiteurs par pays</h4>
                        <div className="flex gap-2">
                          <button onClick={() => exportCSV(countriesData.map(c => ({ Pays: countryNames[c.country?.toUpperCase()] || c.country, Code: c.country, Vues: c.views, Uniques: c.unique_visitors })), 'analytics_pays')}
                            className="flex items-center gap-1 text-muted-foreground hover:text-foreground text-xs"><Download className="w-3 h-3" /> CSV</button>
                          <button onClick={() => exportPDF(countriesData.map(c => ({ Pays: countryNames[c.country?.toUpperCase()] || c.country, Code: c.country, Vues: c.views, Uniques: c.unique_visitors })), 'analytics_pays', 'Visiteurs par Pays')}
                            className="flex items-center gap-1 text-muted-foreground hover:text-foreground text-xs"><FileDown className="w-3 h-3" /> PDF</button>
                        </div>
                      </div>
                      {countriesData.length > 0 ? (
                        <div className="overflow-x-auto max-h-72 overflow-y-auto">
                          <table className="w-full text-xs" data-testid="analytics-countries-table">
                            <thead className="sticky top-0 bg-card z-10">
                              <tr className="text-muted-foreground uppercase tracking-wider border-b border-border">
                                <th className="text-left py-1.5 pr-3">Pays</th>
                                <th className="text-right py-1.5 pr-3">Pages vues</th>
                                <th className="text-right py-1.5">Visiteurs uniques</th>
                              </tr>
                            </thead>
                            <tbody>
                              {countriesData.map((c, i) => (
                                <tr key={i} className="border-b border-border/30 last:border-0 hover:bg-secondary/30 transition-colors" data-testid={`country-row-${i}`}>
                                  <td className="py-2 pr-3">
                                    <div className="flex items-center gap-2">
                                      <FlagIcon code={c.country} size={14} />
                                      <span className="text-foreground/80">{countryNames[c.country?.toUpperCase()] || c.country}</span>
                                      <span className="text-muted-foreground/50 text-[10px]">{c.country?.toUpperCase()}</span>
                                    </div>
                                  </td>
                                  <td className="py-2 pr-3 text-right text-foreground font-medium">{c.views?.toLocaleString()}</td>
                                  <td className="py-2 text-right text-muted-foreground">{c.unique_visitors?.toLocaleString()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-muted-foreground text-xs text-center py-4">Aucune donnée</p>
                      )}
                      {/* Purge XX data */}
                      {countriesData.some(c => c.country === 'XX') && (
                        <div className="mt-3 pt-3 border-t border-border/50">
                          {!purgePreview ? (
                            <button onClick={async () => {
                              try {
                                const r = await axios.get(`${API}/admin/purge/pageviews/preview?country=XX`, { headers });
                                setPurgePreview(r.data);
                              } catch {}
                            }} className="flex items-center gap-1.5 text-xs text-amber-400 hover:text-amber-300 transition-colors" data-testid="purge-xx-preview-btn">
                              <Trash2 className="w-3 h-3" /> Purger les {countriesData.find(c => c.country === 'XX')?.views || 0} vues "Inconnu (XX)"
                            </button>
                          ) : (
                            <div className="flex items-center gap-3 p-2 rounded-lg bg-amber-500/5 border border-amber-500/20">
                              <p className="text-xs text-amber-400">Supprimer {purgePreview.count} page views "XX" ?</p>
                              <button onClick={async () => {
                                setPurging(true);
                                try {
                                  await axios.delete(`${API}/admin/purge/pageviews?country=XX`, { headers });
                                  toast.success(`${purgePreview.count} vues "XX" supprimées`);
                                  setPurgePreview(null);
                                  loadAnalytics();
                                } catch { toast.error('Erreur lors de la purge'); }
                                finally { setPurging(false); }
                              }} disabled={purging}
                                className="px-3 py-1 rounded-md text-xs bg-red-600 hover:bg-red-500 text-white transition-colors flex items-center gap-1" data-testid="purge-xx-confirm-btn">
                                {purging ? <><Loader2 className="w-3 h-3 animate-spin" /> Suppression...</> : 'Confirmer'}
                              </button>
                              <button onClick={() => setPurgePreview(null)} className="text-xs text-muted-foreground hover:text-foreground transition-colors">Annuler</button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="space-y-4">
                    <div className="bg-card border border-border rounded-xl p-4" data-testid="analytics-pages">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-foreground text-xs font-medium">Pages populaires</h4>
                        <button onClick={() => exportCSV(pagesData.map(p => ({ Page: p.path, Vues: p.views })), 'analytics_pages')}
                          className="text-muted-foreground hover:text-foreground"><Download className="w-3 h-3" /></button>
                      </div>
                      <div className="space-y-1.5 max-h-48 overflow-y-auto">
                        {pagesData.map((p, i) => (
                          <div key={i} className="flex items-center justify-between text-xs">
                            <span className="text-foreground/80 truncate max-w-[120px]">{p.path}</span>
                            <span className="text-muted-foreground">{p.views}</span>
                          </div>
                        ))}
                        {!pagesData.length && <p className="text-muted-foreground text-xs">Aucune donnée</p>}
                      </div>
                    </div>
                    <div className="bg-card border border-border rounded-xl p-4" data-testid="analytics-events">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-foreground text-xs font-medium">Événements</h4>
                        <button onClick={() => exportCSV(eventsData.map(e => ({ Événement: e.event, Count: e.count })), 'analytics_events')}
                          className="text-muted-foreground hover:text-foreground"><Download className="w-3 h-3" /></button>
                      </div>
                      <div className="space-y-1.5 max-h-48 overflow-y-auto">
                        {eventsData.map((e, i) => (
                          <div key={i} className="flex items-center justify-between text-xs">
                            <span className="text-foreground/80">{e.event}</span>
                            <span className="text-muted-foreground">{e.count}</span>
                          </div>
                        ))}
                        {!eventsData.length && <p className="text-muted-foreground text-xs">Aucune donnée</p>}
                      </div>
                    </div>
                    </div>
                  </div>
                </>
              )}
              {!analyticsData && <p className="text-muted-foreground text-sm text-center py-8">Chargement...</p>}
            </div>
          )}


          {activeTab === 'updates' && (
            <div data-testid="admin-updates-tab">
              <h3 className="text-foreground font-medium text-sm mb-4">Gestion des mises à jour</h3>
              {!editingUpdate && (
                <div className="p-4 bg-card border border-border rounded-xl mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Plateforme</label>
                      <select
                        value={updateForm.platform}
                        onChange={e => setUpdateForm({...updateForm, platform: e.target.value})}
                        className="w-full bg-secondary/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground"
                        data-testid="update-platform-select"
                      >
                        <option value="">Choisir une plateforme</option>
                        <option value="windows">Windows</option>
                        <option value="macos">macOS</option>
                        <option value="linux">Linux</option>
                        <option value="android">Android</option>
                        <option value="ios">iOS</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Version</label>
                      <input
                        value={updateForm.version}
                        onChange={e => setUpdateForm({...updateForm, version: e.target.value})}
                        placeholder="ex: 1.2.0"
                        className="w-full bg-secondary/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground"
                        data-testid="update-version-input"
                      />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="text-xs text-muted-foreground mb-1 block">Fichier de mise à jour</label>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={e => setUpdateForm({...updateForm, file: e.target.files[0]})}
                      className="w-full bg-secondary/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground file:mr-4 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:bg-blue-600 file:text-white file:cursor-pointer"
                      data-testid="update-file-input"
                    />
                    {updateForm.file && <span className="text-[10px] text-muted-foreground mt-1 block">{updateForm.file.name} ({(updateForm.file.size / (1024*1024)).toFixed(1)} MB)</span>}
                  </div>
                  <textarea
                    value={updateForm.release_notes}
                    onChange={e => setUpdateForm({...updateForm, release_notes: e.target.value})}
                    placeholder="Notes de version (changelog)"
                    rows={3}
                    className="w-full bg-secondary/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground mb-3"
                    data-testid="update-release-notes-input"
                  />
                  <button onClick={async () => {
                    if (!updateForm.platform || !updateForm.version || !updateForm.file) return;
                    setUploadingUpdate(true);
                    setUploadProgress(0);
                    setUploadStage('Préparation...');
                    try {
                      // Step 1: Get presigned URL
                      const presignFd = new FormData();
                      presignFd.append('product_slug', updateForm.platform);
                      presignFd.append('version', updateForm.version);
                      presignFd.append('file_name', updateForm.file.name);
                      presignFd.append('file_size', updateForm.file.size);
                      presignFd.append('content_type', updateForm.file.type || 'application/octet-stream');
                      presignFd.append('release_notes', updateForm.release_notes);
                      const presignRes = await axios.post(`${API}/admin/updates/presign`, presignFd);
                      const { presigned_url, update_id, s3_key, download_url, file_size_str } = presignRes.data;
                      setUploadStage('Envoi du fichier...');

                      // Step 2: Try direct S3 upload first, fallback to backend upload
                      let usePresigned = true;
                      try {
                        await new Promise((resolve, reject) => {
                          const xhr = new XMLHttpRequest();
                          xhr.open('PUT', presigned_url);
                          xhr.setRequestHeader('Content-Type', updateForm.file.type || 'application/octet-stream');
                          xhr.upload.onprogress = (e) => {
                            if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100));
                          };
                          xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`S3: ${xhr.status}`));
                          xhr.onerror = () => reject(new Error('S3 CORS'));
                          xhr.ontimeout = () => reject(new Error('S3 timeout'));
                          xhr.timeout = 600000;
                          xhr.send(updateForm.file);
                        });
                      } catch (s3Err) {
                        // Fallback: upload via backend
                        usePresigned = false;
                        setUploadStage('Envoi via serveur...');
                        setUploadProgress(0);
                        const fd = new FormData();
                        fd.append('file', updateForm.file);
                        fd.append('product_slug', updateForm.platform);
                        fd.append('version', updateForm.version);
                        fd.append('release_notes', updateForm.release_notes);
                        await axios.post(`${API}/admin/updates`, fd, {
                          timeout: 600000,
                          onUploadProgress: (e) => {
                            if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100));
                          },
                        });
                      }

                      // Step 3: Confirm presigned upload (only if presigned was used)
                      if (usePresigned) {
                        setUploadStage('Finalisation...');
                        setUploadProgress(100);
                        await axios.post(`${API}/admin/updates/confirm`, {
                          update_id, s3_key, product_slug: updateForm.platform, version: updateForm.version,
                          release_notes: updateForm.release_notes, download_url,
                          file_name: updateForm.file.name, file_size_str, file_size_bytes: updateForm.file.size,
                        });
                      }

                      setUpdateForm({ platform: '', version: '', release_notes: '', file: null });
                      if (fileInputRef.current) fileInputRef.current.value = '';
                      const r = await axios.get(`${API}/admin/updates`, { headers });
                      setUpdatesData(r.data.updates || []);
                      toast.success('Mise à jour publiée avec succès');
                    } catch (err) {
                      const msg = err.response?.data?.detail || err.message || 'Erreur lors du téléchargement';
                      toast.error(msg);
                    } finally {
                      setUploadingUpdate(false);
                      setUploadProgress(0);
                      setUploadStage('');
                    }
                  }} disabled={uploadingUpdate || !updateForm.platform || !updateForm.version || !updateForm.file}
                    className={`px-4 py-2 rounded-lg text-xs text-white transition-colors flex items-center gap-2 ${uploadingUpdate ? 'bg-emerald-700 cursor-wait' : !updateForm.platform || !updateForm.version || !updateForm.file ? 'bg-emerald-800/50 cursor-not-allowed opacity-50' : 'bg-emerald-600 hover:bg-emerald-500'}`} data-testid="update-add-btn">
                    {uploadingUpdate ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {uploadStage} {uploadProgress > 0 && uploadProgress < 100 ? `${uploadProgress}%` : ''}</> : 'Publier la mise à jour'}
                  </button>
                  {uploadingUpdate && uploadProgress > 0 && (
                    <div className="mt-2 w-full bg-secondary/50 rounded-full h-2 overflow-hidden" data-testid="upload-progress-bar">
                      <div className="bg-emerald-500 h-full rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                    </div>
                  )}
                </div>
              )}

              {editingUpdate && (
                <div className="p-4 bg-card border border-border rounded-xl mb-6">
                  <h4 className="text-foreground text-sm font-medium mb-3">Modifier v{editingUpdate.version} ({editingUpdate.product_slug})</h4>
                  <textarea
                    value={editingUpdate.release_notes}
                    onChange={e => setEditingUpdate({...editingUpdate, release_notes: e.target.value})}
                    placeholder="Notes de version"
                    rows={3}
                    className="w-full bg-secondary/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground mb-3"
                  />
                  <div className="flex gap-2">
                    <button onClick={async () => {
                      const { id, ...payload } = editingUpdate;
                      await axios.put(`${API}/admin/updates/${id}`, { release_notes: payload.release_notes, is_latest: payload.is_latest }, { headers });
                      setEditingUpdate(null);
                      const r = await axios.get(`${API}/admin/updates`, { headers });
                      setUpdatesData(r.data.updates || []);
                    }} className="px-4 py-2 rounded-lg text-xs bg-blue-600 hover:bg-blue-500 text-white transition-colors" data-testid="update-save-btn">Enregistrer</button>
                    <button onClick={() => setEditingUpdate(null)} className="px-4 py-2 rounded-lg text-xs border border-border text-muted-foreground hover:text-foreground transition-colors">Annuler</button>
                  </div>
                </div>
              )}

              {/* Group updates by platform */}
              {(() => {
                const grouped = {};
                const platformNames = { windows: 'Windows', macos: 'macOS', linux: 'Linux', android: 'Android', ios: 'iOS' };
                const platformIcons = { windows: Monitor, macos: Monitor, linux: Monitor, android: Smartphone, ios: Smartphone };
                updatesData.forEach(u => {
                  const key = u.product_slug;
                  if (!grouped[key]) grouped[key] = [];
                  grouped[key].push(u);
                });
                return Object.entries(grouped).map(([slug, versions]) => {
                  const PIcon = platformIcons[slug] || PackageOpen;
                  return (
                    <div key={slug} className="mb-6">
                      <h4 className="text-foreground text-sm font-medium mb-2 flex items-center gap-2">
                        <PIcon className="w-4 h-4 text-blue-400" />
                        {platformNames[slug] || slug}
                      </h4>
                      <div className="space-y-2">
                        {versions.map(upd => (
                          <div key={upd.id} className="flex items-center gap-3 p-3 bg-card border border-border rounded-xl" data-testid={`update-item-${upd.id}`}>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-mono ${upd.is_latest ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-secondary text-muted-foreground'}`}>
                              v{upd.version}
                            </span>
                            {upd.is_latest && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400">Dernière</span>}
                            <div className="flex-1 min-w-0">
                              <p className="text-muted-foreground text-xs truncate">{upd.release_notes || 'Pas de notes'}</p>
                              <div className="flex items-center gap-3 mt-0.5">
                                {upd.file_name && <span className="text-[10px] text-muted-foreground/60">{upd.file_name}</span>}
                                {upd.file_size && <span className="text-[10px] text-muted-foreground/60">{upd.file_size}</span>}
                                {upd.min_version && <span className="text-[10px] text-muted-foreground/60">min: v{upd.min_version}</span>}
                                {upd.download_url && <a href={upd.download_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-400 hover:text-blue-300">Télécharger</a>}
                                <span className="text-[10px] text-muted-foreground/40">{upd.created_at ? new Date(upd.created_at).toLocaleDateString('fr-FR') : ''}</span>
                              </div>
                            </div>
                            <button onClick={() => setEditingUpdate({...upd})} className="text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid={`update-edit-${upd.id}`}>Modifier</button>
                            <button onClick={async () => {
                              if (!window.confirm('Supprimer cette version ?')) return;
                              await axios.delete(`${API}/admin/updates/${upd.id}`, { headers });
                              const r = await axios.get(`${API}/admin/updates`, { headers });
                              setUpdatesData(r.data.updates || []);
                            }} className="text-xs text-red-400 hover:text-red-300 transition-colors" data-testid={`update-delete-${upd.id}`}>Supprimer</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                });
              })()}
              {updatesData.length === 0 && <p className="text-muted-foreground text-sm text-center py-8">Aucune mise à jour publiée</p>}
            </div>
          )}

          {activeTab === 'reports' && (
            <div data-testid="admin-reports-tab">
              {/* Weekly Report Preview + Config */}
              <div className="mb-6 p-5 bg-card border border-border rounded-xl">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-foreground font-medium text-sm flex items-center gap-2"><Mail className="w-4 h-4 text-blue-400" /> Rapport hebdomadaire</h3>
                  <div className="flex gap-2">
                    <button onClick={async () => {
                      const r = await axios.get(`${API}/admin/report-preview`, { headers });
                      setReportPreview(r.data); setReportConfig(r.data.config);
                    }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-border text-muted-foreground hover:text-foreground transition-colors" data-testid="report-preview-btn">
                      <Eye className="w-3.5 h-3.5" /> Previsualiser
                    </button>
                    <button onClick={async () => {
                      setReportSending(true);
                      await axios.post(`${API}/admin/report-send`, {}, { headers }).catch(() => {});
                      setReportSending(false);
                    }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-blue-600 hover:bg-blue-500 text-white transition-colors" data-testid="report-send-btn">
                      <Send className="w-3.5 h-3.5" /> {reportSending ? 'Envoi...' : 'Envoyer maintenant'}
                    </button>
                  </div>
                </div>
                {/* Config */}
                {reportConfig && (
                  <div className="flex items-center gap-3 mb-4 p-3 rounded-lg bg-secondary/50">
                    <Settings className="w-4 h-4 text-muted-foreground" />
                    <input type="email" value={reportConfig.email || ''} onChange={e => setReportConfig({...reportConfig, email: e.target.value})}
                      className="flex-1 bg-transparent text-foreground text-sm outline-none" placeholder="Email du rapport" data-testid="report-email-input" />
                    <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <input type="checkbox" checked={reportConfig.enabled || false} onChange={e => setReportConfig({...reportConfig, enabled: e.target.checked})} className="accent-blue-500" data-testid="report-enabled-checkbox" /> Actif
                    </label>
                    <button onClick={async () => {
                      await axios.put(`${API}/admin/report-config`, { email: reportConfig.email, enabled: reportConfig.enabled }, { headers });
                    }} className="px-3 py-1 rounded-lg text-xs bg-emerald-600 hover:bg-emerald-500 text-white transition-colors" data-testid="report-save-config-btn">Sauver</button>
                  </div>
                )}
                {/* Preview */}
                {reportPreview && (
                  <div className="space-y-4" data-testid="report-preview-content">
                    <p className="text-muted-foreground text-xs">Periode: {new Date(reportPreview.period.from).toLocaleDateString('fr-FR')} - {new Date(reportPreview.period.to).toLocaleDateString('fr-FR')}</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {[
                        { label: 'Pages vues', value: reportPreview.kpis.total_views, color: 'text-blue-400' },
                        { label: 'Visiteurs uniques', value: reportPreview.kpis.unique_visitors, color: 'text-emerald-400' },
                        { label: 'Bots', value: reportPreview.kpis.bot_views, color: 'text-amber-400' },
                        { label: 'Transactions', value: reportPreview.kpis.transactions, color: 'text-violet-400' },
                        { label: 'Revenus', value: `${reportPreview.kpis.revenue} EUR`, color: 'text-emerald-400' },
                        { label: 'Nouveaux users', value: reportPreview.kpis.new_users, color: 'text-cyan-400' },
                      ].map((k, i) => (
                        <div key={i} className="p-3 rounded-lg bg-secondary/30 border border-border">
                          <p className="text-muted-foreground text-[10px] uppercase tracking-wider">{k.label}</p>
                          <p className={`text-xl font-bold ${k.color}`}>{k.value}</p>
                        </div>
                      ))}
                    </div>
                    {reportPreview.top_countries.length > 0 && (
                      <div>
                        <h4 className="text-foreground text-xs font-medium mb-2">Top pays</h4>
                        <div className="flex gap-2 flex-wrap">
                          {reportPreview.top_countries.map((c, i) => (
                            <span key={i} className="text-xs px-2 py-1 rounded-full bg-secondary border border-border text-foreground/80">{c.country} ({c.count})</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {reportPreview.top_pages.length > 0 && (
                      <div>
                        <h4 className="text-foreground text-xs font-medium mb-2">Top pages</h4>
                        <div className="flex gap-2 flex-wrap">
                          {reportPreview.top_pages.map((p, i) => (
                            <span key={i} className="text-xs px-2 py-1 rounded-full bg-secondary border border-border text-foreground/80">{p.page} ({p.count})</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <h3 className="text-foreground font-medium text-sm mb-4">Exporter les rapports</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { label: 'Utilisateurs', icon: Users, fetch: () => axios.get(`${API}/admin/users-licenses`, { headers }).then(r =>
                    (r.data.users || []).map(u => ({ Nom: u.name || '-', Email: u.email, Licences: u.entitlements_count, Appareils: u.activations_count }))
                  ), file: 'utilisateurs', title: 'Utilisateurs' },
                  { label: 'Transactions', icon: CreditCard, fetch: () => axios.get(`${API}/admin/transactions`, { headers }).then(r =>
                    (r.data.transactions || []).map(t => ({ Email: t.user_email, Package: t.package_id, Montant: t.amount, Devise: t.currency, Promo: t.promo_code || '', Statut: t.payment_status, Date: t.created_at?.slice(0, 10) }))
                  ), file: 'transactions', title: 'Transactions' },
                  { label: 'Campagnes', icon: Tag, fetch: () => axios.get(`${API}/admin/campaigns`, { headers }).then(r =>
                    (r.data.campaigns || []).map(c => ({ Nom: c.name, Statut: c.status, Reduction: `${c.discount_value}${c.discount_type === 'percentage' ? '%' : ' EUR'}`, Debut: c.start_date?.slice(0, 10), Fin: c.end_date?.slice(0, 10) }))
                  ), file: 'campagnes', title: 'Campagnes Marketing' },
                  { label: 'Alertes fraude', icon: AlertTriangle, fetch: () => axios.get(`${API}/admin/fraud-alerts`, { headers }).then(r =>
                    (r.data.alerts || []).map(a => ({ Type: a.type, Severite: a.severity, Statut: a.status, Message: a.message, Email: a.user_email || '', Date: a.detected_at?.slice(0, 10) }))
                  ), file: 'alertes_fraude', title: 'Alertes Fraude' },
                  { label: 'Visiteurs (pays)', icon: Globe, fetch: () => axios.get(`${API}/admin/analytics/countries?days=365`, { headers }).then(r =>
                    (r.data.countries || []).map(c => ({ Pays: c.country, Vues: c.views, Visiteurs_uniques: c.unique_visitors }))
                  ), file: 'visiteurs_pays', title: 'Visiteurs par Pays' },
                  { label: 'Pages populaires', icon: Activity, fetch: () => axios.get(`${API}/admin/analytics/pages?days=365`, { headers }).then(r =>
                    (r.data.pages || []).map(p => ({ Page: p.path, Vues: p.views }))
                  ), file: 'pages_populaires', title: 'Pages Populaires' },
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <div key={i} className="p-4 bg-card border border-border rounded-xl hover:border-blue-500/30 transition-colors" data-testid={`export-report-${i}`}>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                          <Icon className="w-5 h-5 text-blue-400" />
                        </div>
                        <p className="text-foreground text-sm font-medium">{item.label}</p>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => item.fetch().then(d => exportCSV(d, item.file))}
                          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-border text-muted-foreground hover:text-foreground transition-colors"
                          data-testid={`export-report-csv-${i}`}>
                          <Download className="w-3.5 h-3.5" /> CSV
                        </button>
                        <button onClick={() => item.fetch().then(d => exportPDF(d, item.file, item.title))}
                          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-border text-muted-foreground hover:text-foreground transition-colors"
                          data-testid={`export-report-pdf-${i}`}>
                          <FileDown className="w-3.5 h-3.5" /> PDF
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Referrals Tab */}
          {activeTab === 'referrals' && (
            <div data-testid="admin-referrals-tab">
              <h3 className="text-foreground font-medium text-sm mb-4">Suivi des parrainages</h3>
              {referralData && (
                <>
                  <div className="grid grid-cols-3 gap-3 mb-6">
                    <div className="p-4 rounded-xl bg-card border border-border">
                      <p className="text-muted-foreground text-[10px] uppercase tracking-wider">Parrains</p>
                      <p className="text-2xl font-bold text-blue-400">{referralData.stats.total_referrers}</p>
                    </div>
                    <div className="p-4 rounded-xl bg-card border border-border">
                      <p className="text-muted-foreground text-[10px] uppercase tracking-wider">Événements</p>
                      <p className="text-2xl font-bold text-emerald-400">{referralData.stats.total_events}</p>
                    </div>
                    <div className="p-4 rounded-xl bg-card border border-border">
                      <p className="text-muted-foreground text-[10px] uppercase tracking-wider">Achats via parrainage</p>
                      <p className="text-2xl font-bold text-violet-400">{referralData.stats.purchase_conversions}</p>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 mb-3">
                    <button onClick={() => exportCSV(referralData.referrers.map(r => ({
                      Email: r.user_email, Code: r.referral_code, Parrainages: r.total_referrals, Date: r.created_at?.slice(0, 10),
                    })), 'parrainages')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-border text-muted-foreground hover:text-foreground transition-colors" data-testid="export-referrals-csv">
                      <Download className="w-3.5 h-3.5" /> CSV
                    </button>
                    <button onClick={() => exportPDF(referralData.referrers.map(r => ({
                      Email: r.user_email, Code: r.referral_code, Parrainages: r.total_referrals, Date: r.created_at?.slice(0, 10),
                    })), 'parrainages', 'Parrainages')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-border text-muted-foreground hover:text-foreground transition-colors" data-testid="export-referrals-pdf">
                      <FileDown className="w-3.5 h-3.5" /> PDF
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="admin-referrals-table">
                      <thead>
                        <tr className="text-muted-foreground text-xs uppercase tracking-wider border-b border-border">
                          <th className="text-left py-2 pr-4">Email</th>
                          <th className="text-left py-2 pr-4">Code</th>
                          <th className="text-left py-2 pr-4">Parrainages</th>
                          <th className="text-left py-2">Inscrit le</th>
                        </tr>
                      </thead>
                      <tbody>
                        {referralData.referrers.map((r, i) => (
                          <tr key={i} className="border-b border-border/50 last:border-0">
                            <td className="py-2.5 pr-4 text-foreground/80">{r.user_email}</td>
                            <td className="py-2.5 pr-4"><span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 font-mono">{r.referral_code}</span></td>
                            <td className="py-2.5 pr-4 text-foreground font-medium">{r.total_referrals}</td>
                            <td className="py-2.5 text-muted-foreground">{r.created_at ? new Date(r.created_at).toLocaleDateString('fr-FR') : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {referralData.events.length > 0 && (
                    <div className="mt-6">
                      <h4 className="text-foreground text-xs font-medium mb-3">Derniers événements</h4>
                      <div className="space-y-2">
                        {referralData.events.slice(0, 20).map((e, i) => (
                          <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-secondary/30 text-xs">
                            <span className={`px-2 py-0.5 rounded-full ${e.event_type === 'purchase' ? 'bg-emerald-500/10 text-emerald-400' : e.event_type === 'register' ? 'bg-blue-500/10 text-blue-400' : 'bg-secondary text-muted-foreground'}`}>{e.event_type}</span>
                            <span className="text-muted-foreground">{e.referred_email || '-'}</span>
                            <span className="text-muted-foreground ml-auto">{e.created_at ? new Date(e.created_at).toLocaleDateString('fr-FR') : ''}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Roadmap Tab */}
          {activeTab === 'roadmap' && (
            <div data-testid="admin-roadmap-tab">
              <h3 className="text-foreground font-medium text-sm mb-4">Gestion de la Roadmap</h3>
              {/* Add/Edit form */}
              <div className="p-4 bg-card border border-border rounded-xl mb-6">
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <input value={editingRoadmap ? editingRoadmap.title : roadmapForm.title} onChange={e => editingRoadmap ? setEditingRoadmap({...editingRoadmap, title: e.target.value}) : setRoadmapForm({...roadmapForm, title: e.target.value})}
                    placeholder="Titre" className="bg-secondary/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="roadmap-title-input" />
                  <select value={editingRoadmap ? editingRoadmap.status : roadmapForm.status} onChange={e => editingRoadmap ? setEditingRoadmap({...editingRoadmap, status: e.target.value}) : setRoadmapForm({...roadmapForm, status: e.target.value})}
                    className="bg-secondary/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="roadmap-status-select">
                    <option value="planned">Planifié</option>
                    <option value="in_progress">En cours</option>
                    <option value="completed">Terminé</option>
                    <option value="cancelled">Annulé</option>
                  </select>
                </div>
                <input value={editingRoadmap ? editingRoadmap.description : roadmapForm.description} onChange={e => editingRoadmap ? setEditingRoadmap({...editingRoadmap, description: e.target.value}) : setRoadmapForm({...roadmapForm, description: e.target.value})}
                  placeholder="Description" className="w-full bg-secondary/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground mb-3" data-testid="roadmap-desc-input" />
                <div className="flex gap-2">
                  <select value={editingRoadmap ? editingRoadmap.category : roadmapForm.category} onChange={e => editingRoadmap ? setEditingRoadmap({...editingRoadmap, category: e.target.value}) : setRoadmapForm({...roadmapForm, category: e.target.value})}
                    className="bg-secondary/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="roadmap-category-select">
                    <option value="feature">Feature</option>
                    <option value="improvement">Amélioration</option>
                    <option value="bugfix">Bugfix</option>
                    <option value="integration">Intégration</option>
                  </select>
                  {editingRoadmap ? (
                    <>
                      <button onClick={async () => {
                        await axios.put(`${API}/admin/roadmap/${editingRoadmap.id}`, editingRoadmap, { headers });
                        setEditingRoadmap(null);
                        const r = await axios.get(`${API}/admin/roadmap`, { headers });
                        setRoadmapItems(r.data.items || []);
                      }} className="px-4 py-2 rounded-lg text-xs bg-blue-600 hover:bg-blue-500 text-white transition-colors" data-testid="roadmap-update-btn">Modifier</button>
                      <button onClick={() => setEditingRoadmap(null)} className="px-4 py-2 rounded-lg text-xs border border-border text-muted-foreground hover:text-foreground transition-colors">Annuler</button>
                    </>
                  ) : (
                    <button onClick={async () => {
                      if (!roadmapForm.title) return;
                      await axios.post(`${API}/admin/roadmap`, roadmapForm, { headers });
                      setRoadmapForm({ title: '', description: '', status: 'planned', category: 'feature' });
                      const r = await axios.get(`${API}/admin/roadmap`, { headers });
                      setRoadmapItems(r.data.items || []);
                    }} className="px-4 py-2 rounded-lg text-xs bg-emerald-600 hover:bg-emerald-500 text-white transition-colors" data-testid="roadmap-add-btn">Ajouter</button>
                  )}
                </div>
              </div>
              {/* Items list */}
              <div className="space-y-2">
                {roadmapItems.map((item) => (
                  <div key={item.id} className="flex items-center gap-3 p-3 bg-card border border-border rounded-xl" data-testid={`roadmap-item-${item.id}`}>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      item.status === 'completed' ? 'bg-emerald-500/10 text-emerald-400' :
                      item.status === 'in_progress' ? 'bg-blue-500/10 text-blue-400' :
                      item.status === 'cancelled' ? 'bg-red-500/10 text-red-400' :
                      'bg-secondary text-muted-foreground'
                    }`}>{item.status}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-violet-500/10 text-violet-400">{item.category}</span>
                    <div className="flex-1">
                      <p className="text-foreground text-sm font-medium">{item.title}</p>
                      {item.description && <p className="text-muted-foreground text-xs">{item.description}</p>}
                    </div>
                    <button onClick={() => setEditingRoadmap({...item})} className="text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid={`roadmap-edit-${item.id}`}>Modifier</button>
                    <button onClick={async () => {
                      await axios.delete(`${API}/admin/roadmap/${item.id}`, { headers });
                      setRoadmapItems(roadmapItems.filter(r => r.id !== item.id));
                    }} className="text-xs text-red-400 hover:text-red-300 transition-colors" data-testid={`roadmap-delete-${item.id}`}>Supprimer</button>
                  </div>
                ))}
                {roadmapItems.length === 0 && <p className="text-muted-foreground text-sm text-center py-8">Aucun élément dans la roadmap</p>}
              </div>

              {/* Changelog Editor */}
              <div className="mt-10 border-t border-border pt-8" data-testid="admin-changelog-section">
                <h3 className="text-foreground font-medium text-sm mb-4">Gestion du Changelog</h3>
                <div className="p-4 bg-card border border-border rounded-xl mb-6">
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <input value={editingChangelog ? editingChangelog.version : changelogForm.version} onChange={e => editingChangelog ? setEditingChangelog({...editingChangelog, version: e.target.value}) : setChangelogForm({...changelogForm, version: e.target.value})}
                      placeholder="Version (ex: v0.4.0)" className="bg-secondary/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="changelog-version-input" />
                    <input value={editingChangelog ? editingChangelog.date : changelogForm.date} onChange={e => editingChangelog ? setEditingChangelog({...editingChangelog, date: e.target.value}) : setChangelogForm({...changelogForm, date: e.target.value})}
                      placeholder="Date (ex: Mars 2026)" className="bg-secondary/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="changelog-date-input" />
                    <input value={editingChangelog ? editingChangelog.tag : changelogForm.tag} onChange={e => editingChangelog ? setEditingChangelog({...editingChangelog, tag: e.target.value}) : setChangelogForm({...changelogForm, tag: e.target.value})}
                      placeholder="Tag (ex: Actuel)" className="bg-secondary/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="changelog-tag-input" />
                  </div>
                  <textarea value={editingChangelog ? (Array.isArray(editingChangelog.items) ? editingChangelog.items.join('\n') : editingChangelog.items) : changelogForm.items}
                    onChange={e => {
                      const val = e.target.value;
                      if (editingChangelog) setEditingChangelog({...editingChangelog, items: val});
                      else setChangelogForm({...changelogForm, items: val});
                    }}
                    placeholder="Éléments (un par ligne)" rows={3}
                    className="w-full bg-secondary/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground mb-3" data-testid="changelog-items-input" />
                  <div className="flex gap-2">
                    {editingChangelog ? (
                      <>
                        <button onClick={async () => {
                          const items = typeof editingChangelog.items === 'string' ? editingChangelog.items.split('\n').filter(Boolean) : editingChangelog.items;
                          await axios.put(`${API}/admin/changelog/${editingChangelog.id}`, {...editingChangelog, items}, { headers });
                          setEditingChangelog(null);
                          const r = await axios.get(`${API}/admin/changelog`, { headers });
                          setChangelogEntries(r.data.entries || []);
                        }} className="px-4 py-2 rounded-lg text-xs bg-blue-600 hover:bg-blue-500 text-white transition-colors" data-testid="changelog-update-btn">Modifier</button>
                        <button onClick={() => setEditingChangelog(null)} className="px-4 py-2 rounded-lg text-xs border border-border text-muted-foreground hover:text-foreground transition-colors">Annuler</button>
                      </>
                    ) : (
                      <button onClick={async () => {
                        if (!changelogForm.version) return;
                        const items = changelogForm.items.split('\n').filter(Boolean);
                        await axios.post(`${API}/admin/changelog`, {...changelogForm, items}, { headers });
                        setChangelogForm({ version: '', date: '', tag: '', items: '' });
                        const r = await axios.get(`${API}/admin/changelog`, { headers });
                        setChangelogEntries(r.data.entries || []);
                      }} className="px-4 py-2 rounded-lg text-xs bg-emerald-600 hover:bg-emerald-500 text-white transition-colors" data-testid="changelog-add-btn">Ajouter</button>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  {changelogEntries.map((entry) => (
                    <div key={entry.id} className="flex items-center gap-3 p-3 bg-card border border-border rounded-xl" data-testid={`changelog-entry-${entry.id}`}>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 font-mono">{entry.version}</span>
                      <span className="text-xs text-muted-foreground">{entry.date}</span>
                      {entry.tag && <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400">{entry.tag}</span>}
                      <div className="flex-1">
                        <p className="text-foreground text-xs">{(entry.items || []).join(' · ')}</p>
                      </div>
                      <button onClick={() => setEditingChangelog({...entry, items: (entry.items || []).join('\n')})} className="text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid={`changelog-edit-${entry.id}`}>Modifier</button>
                      <button onClick={async () => {
                        await axios.delete(`${API}/admin/changelog/${entry.id}`, { headers });
                        setChangelogEntries(changelogEntries.filter(e => e.id !== entry.id));
                      }} className="text-xs text-red-400 hover:text-red-300 transition-colors" data-testid={`changelog-delete-${entry.id}`}>Supprimer</button>
                    </div>
                  ))}
                  {changelogEntries.length === 0 && <p className="text-muted-foreground text-sm text-center py-4">Aucune entrée dans le changelog</p>}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'resources' && (
            <div data-testid="admin-resources-tab">
              <h3 className="text-foreground font-medium text-sm mb-4">Gestion des Ressources</h3>
              <div className="p-4 bg-card border border-border rounded-xl mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                  <input value={editingResource ? editingResource.title : resourceForm.title} onChange={e => editingResource ? setEditingResource({...editingResource, title: e.target.value}) : setResourceForm({...resourceForm, title: e.target.value})}
                    placeholder="Titre" className="bg-secondary/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="resource-title-input" />
                  <select value={editingResource ? editingResource.category : resourceForm.category} onChange={e => editingResource ? setEditingResource({...editingResource, category: e.target.value}) : setResourceForm({...resourceForm, category: e.target.value})}
                    className="bg-secondary/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="resource-category-select">
                    <option value="guide">Guide</option>
                    <option value="tutorial">Tutoriel</option>
                    <option value="faq">FAQ</option>
                    <option value="documentation">Documentation</option>
                    <option value="other">Autre</option>
                  </select>
                  <select value={editingResource ? (editingResource.language || 'all') : (resourceForm.language || 'all')} onChange={e => editingResource ? setEditingResource({...editingResource, language: e.target.value}) : setResourceForm({...resourceForm, language: e.target.value})}
                    className="bg-secondary/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="resource-language-select">
                    <option value="all">Toutes les langues</option>
                    <option value="fr">Français</option>
                    <option value="en">English</option>
                    <option value="es">Español</option>
                    <option value="de">Deutsch</option>
                    <option value="it">Italiano</option>
                    <option value="pt">Português</option>
                    <option value="nl">Nederlands</option>
                    <option value="pl">Polski</option>
                    <option value="tr">Türkçe</option>
                    <option value="ru">Русский</option>
                    <option value="ja">日本語</option>
                    <option value="zh">中文</option>
                    <option value="ar">العربية</option>
                  </select>
                </div>
                <input value={editingResource ? editingResource.description : resourceForm.description} onChange={e => editingResource ? setEditingResource({...editingResource, description: e.target.value}) : setResourceForm({...resourceForm, description: e.target.value})}
                  placeholder="Description courte" className="w-full bg-secondary/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground mb-3" data-testid="resource-description-input" />
                <input value={editingResource ? editingResource.url : resourceForm.url} onChange={e => editingResource ? setEditingResource({...editingResource, url: e.target.value}) : setResourceForm({...resourceForm, url: e.target.value})}
                  placeholder="URL externe (optionnel)" className="w-full bg-secondary/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground mb-3" data-testid="resource-url-input" />
                <textarea value={editingResource ? editingResource.content : resourceForm.content} onChange={e => editingResource ? setEditingResource({...editingResource, content: e.target.value}) : setResourceForm({...resourceForm, content: e.target.value})}
                  placeholder="Contenu détaillé (optionnel)" rows={4}
                  className="w-full bg-secondary/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground mb-3" data-testid="resource-content-input" />
                <div className="flex gap-2">
                  {editingResource ? (
                    <>
                      <button onClick={async () => {
                        await axios.put(`${API}/admin/resources/${editingResource.id}`, editingResource, { headers });
                        setEditingResource(null);
                        const r = await axios.get(`${API}/admin/resources`, { headers });
                        setAdminResources(r.data.resources || []);
                      }} className="px-4 py-2 rounded-lg text-xs bg-blue-600 hover:bg-blue-500 text-white transition-colors" data-testid="resource-update-btn">Modifier</button>
                      <button onClick={() => setEditingResource(null)} className="px-4 py-2 rounded-lg text-xs border border-border text-muted-foreground hover:text-foreground transition-colors">Annuler</button>
                    </>
                  ) : (
                    <button onClick={async () => {
                      if (!resourceForm.title) return;
                      await axios.post(`${API}/admin/resources`, resourceForm, { headers });
                      setResourceForm({ title: '', description: '', category: 'guide', url: '', content: '', language: 'all' });
                      const r = await axios.get(`${API}/admin/resources`, { headers });
                      setAdminResources(r.data.resources || []);
                    }} className="px-4 py-2 rounded-lg text-xs bg-emerald-600 hover:bg-emerald-500 text-white transition-colors" data-testid="resource-add-btn">Ajouter</button>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                {adminResources.map(res => (
                  <div key={res.id} className={`flex items-center gap-3 p-3 bg-card border ${res.pinned ? 'border-amber-500/30 bg-amber-500/5' : 'border-border'} rounded-xl`} data-testid={`resource-item-${res.id}`}>
                    {res.pinned && <span className="text-amber-500 text-xs" title="Épinglé">&#9733;</span>}
                    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 capitalize">{res.category}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground uppercase">{res.language || 'all'}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-foreground text-sm font-medium truncate">{res.title}</p>
                      {res.description && <p className="text-muted-foreground text-xs truncate">{res.description}</p>}
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${res.published ? 'bg-emerald-500/10 text-emerald-400' : 'bg-secondary text-muted-foreground'}`}>
                      {res.published ? 'Publié' : 'Brouillon'}
                    </span>
                    <button onClick={async () => {
                      await axios.put(`${API}/admin/resources/${res.id}`, { pinned: !res.pinned }, { headers });
                      const r2 = await axios.get(`${API}/admin/resources`, { headers });
                      setAdminResources(r2.data.resources || []);
                    }} className={`text-xs transition-colors ${res.pinned ? 'text-amber-500 hover:text-amber-400' : 'text-muted-foreground hover:text-amber-500'}`} data-testid={`resource-pin-${res.id}`} title={res.pinned ? 'Désépingler' : 'Épingler'}>
                      {res.pinned ? 'Désépingler' : 'Épingler'}
                    </button>
                    <button onClick={() => setEditingResource({...res})} className="text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid={`resource-edit-${res.id}`}>Modifier</button>
                    <button onClick={async () => {
                      await axios.delete(`${API}/admin/resources/${res.id}`, { headers });
                      setAdminResources(adminResources.filter(r => r.id !== res.id));
                    }} className="text-xs text-red-400 hover:text-red-300 transition-colors" data-testid={`resource-delete-${res.id}`}>Supprimer</button>
                  </div>
                ))}
                {adminResources.length === 0 && <p className="text-muted-foreground text-sm text-center py-8">Aucune ressource</p>}
              </div>
            </div>
          )}

          {activeTab === 'blog' && <AdminBlogManager />}
        </motion.div>
      </div>
    </div>
  );
}
